/**
 * Shared phrasing and value-formatting helpers for outgoing-intake reviews.
 *
 * These are the single source of truth for how questions are phrased and how
 * answers are displayed, so the foreign-national-facing review (Typeform flow)
 * and the law-firm-facing submission review read identically.
 *
 * All functions here are pure and free of React/UI dependencies.
 */
import { formatDateOnly } from "@/lib/dateOnly";

import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeFormNode
} from "../../../api/types/outgoingIntake";

type AddressValue = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
};

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function flatQuestions(
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] {
  const out: OutgoingIntakeFormNode[] = [];
  const walk = (n: OutgoingIntakeFormNode) => {
    if (n.node_type === "question") out.push(n);
    (n.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}

function asAddressValue(value: unknown): AddressValue {
  if (!value || typeof value !== "object") return {};
  const v = value as Record<string, unknown>;
  return {
    street: typeof v.street === "string" ? v.street : null,
    city: typeof v.city === "string" ? v.city : null,
    state: typeof v.state === "string" ? v.state : null,
    zip_code: typeof v.zip_code === "string" ? v.zip_code : null,
    country: typeof v.country === "string" ? v.country : null
  };
}

/** Sentence-cases a label while preserving acronyms and tokens with digits. */
export function sentenceCaseDocumentLabel(raw: string): string {
  return String(raw ?? "")
    .trim()
    .split(/\s+/)
    .map((word) => {
      const cleaned = word.replace(/^[^\w]+|[^\w]+$/g, "");
      if (!cleaned) return word;
      if (/\d/.test(cleaned) || cleaned.toUpperCase() === cleaned) return word;
      return word.toLowerCase();
    })
    .join(" ");
}

/** Phrases a document/file request as an imperative "Please upload ..." title. */
export function formatDocumentRequestTitle(raw: string): string {
  const value = normalizeText(raw);
  if (!value) return "";
  const withTerminalPunctuation = (text: string): string => {
    const trimmed = text.trim();
    if (!trimmed) return "";
    const requiredSuffix = trimmed.endsWith("*") ? "*" : "";
    const body = requiredSuffix ? trimmed.slice(0, -1).trimEnd() : trimmed;
    if (/[.!?]$/.test(body)) return `${body}${requiredSuffix}`;
    return `${body}.${requiredSuffix}`;
  };

  if (
    /[.!?]$/.test(value) ||
    /^(please|upload|provide|submit|attach|include|share|send|add)\b/i.test(
      value
    )
  ) {
    return withTerminalPunctuation(value);
  }
  return withTerminalPunctuation(
    `Please upload your ${sentenceCaseDocumentLabel(value)}`
  );
}

/** Phrases an information request as a conversational question. */
export function formatInformationRequestTitle(raw: string): string {
  const value = normalizeText(raw);
  if (!value) return "";
  if (/[?]$/.test(value)) return value;

  const lower = value.toLowerCase();
  if (
    lower.includes("immigration status") &&
    (lower.includes("expiration") || lower.includes("expiry"))
  ) {
    return "When does your current immigration status expire?";
  }
  if (lower.includes("expiration") || lower.includes("expiry")) {
    const expiringThing = value
      .replace(/\b(expiration date|expiry date|expiration|expiry)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    if (expiringThing) {
      return `When does your ${sentenceCaseDocumentLabel(
        expiringThing
      )} expire?`;
    }
  }

  const alreadyConversational =
    /^(what|which|who|when|where|why|how|do|does|did|is|are|have|has|can|could|would|should|please|tell|list|provide|enter|select|choose|if)\b/.test(
      lower
    );
  const wordCount = value.split(/\s+/).filter(Boolean).length;

  if (alreadyConversational || wordCount > 5) {
    return /[.!?]$/.test(value) ? value : `${value}?`;
  }

  // Open-ended notes/comments fields don't fit the "What is your ..." template
  // (e.g. "What is your notes?"), so phrase them naturally.
  if (
    /^(additional |other |extra )?(notes?|comments?|remarks?)$/i.test(value)
  ) {
    return "Is there anything else you'd like to add?";
  }

  const label = sentenceCaseDocumentLabel(value);
  const pluralLabelPattern =
    /\b(pronouns|addresses|documents|files|dates|preferences|notes|comments|remarks|details)\b/i;
  return pluralLabelPattern.test(value)
    ? `What are your ${label}?`
    : `What is your ${label}?`;
}

/**
 * Canonical statement-style question title for a repeatable group, so it reads
 * like any other question (e.g. "Tell us about your employment history.").
 */
export function groupQuestionTitle(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") {
    return "Tell us about your address history.";
  }
  if (groupId === "employment_history") {
    return "Tell us about your employment history.";
  }
  if (groupId === "immigration_status_history") {
    return "Tell us about your immigration status history.";
  }
  if (groupId === "immigration_entry_exit_history") {
    return "Tell us about your U.S. entry and exit history.";
  }
  if (groupId === "dependents") return "Tell us about your dependents.";
  const base = normalizeText(group.title) || "entries";
  return `Tell us about your ${base.toLowerCase()}.`;
}

/** True when a question is a free-form notes/comments field. */
export function isNotesQuestion(node: OutgoingIntakeFormNode): boolean {
  const label = `${normalizeText(node.title)} ${normalizeText(node.prompt)} ${
    node.node_id
  }`;
  return /\b(notes?|comments?|remarks?)\b/i.test(label);
}

/**
 * Description shown beneath a question. Open-ended notes/comments fields rarely
 * carry help text from the template, so fall back to explaining what the field
 * is for. Inside a repeatable group the copy references "this entry".
 */
export function informationRequestDescription(
  node: OutgoingIntakeFormNode,
  inGroup: boolean
): string {
  const help = normalizeText(node.help_text);
  if (help) return help;
  if (isNotesQuestion(node)) {
    return inGroup
      ? "Share any additional information or explanation about this entry."
      : "Share any additional information or explanation.";
  }
  return "";
}

/** Singular, human label for one entry of a repeatable group. */
export function groupEntryLabel(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") return "Address";
  if (groupId === "employment_history") return "Employer";
  if (groupId === "immigration_status_history") return "Status";
  if (groupId === "immigration_entry_exit_history") return "Trip";
  return normalizeText(group.title) || "Entry";
}

/** Ordered leaf sub-questions for one entry of a repeatable group. */
export function groupChildQuestions(
  group: OutgoingIntakeFormNode
): OutgoingIntakeFormNode[] {
  return flatQuestions(group.children ?? []);
}

/**
 * A short, human label identifying one entry from its most descriptive answer
 * (currently the street/city of an address sub-question). Returns null when no
 * such context is available yet.
 */
export function groupEntryContextLabel(
  group: OutgoingIntakeFormNode,
  entryAnswers: Record<string, OutgoingIntakeAnswer>
): string | null {
  const addressQuestion = groupChildQuestions(group).find(
    (q) => q.question_type === "address"
  );
  if (addressQuestion) {
    const addr = asAddressValue(entryAnswers[addressQuestion.node_id]?.value);
    const street = normalizeText(addr.street);
    if (street) return street;
    const city = normalizeText(addr.city);
    if (city) return city;
  }
  return null;
}

/**
 * Rewrites a repeatable-group sub-question so it references context the client
 * already provided for the same entry, e.g. "When did you start living at 123
 * Main St?" instead of "What is your start date?". Returns null when there's no
 * useful context, so callers fall back to the plain prompt.
 */
export function contextualGroupQuestionTitle(
  group: OutgoingIntakeFormNode,
  node: OutgoingIntakeFormNode,
  entryAnswers: Record<string, OutgoingIntakeAnswer>
): string | null {
  const label = groupEntryContextLabel(group, entryAnswers);
  if (!label) return null;

  const hint = `${normalizeText(node.prompt)} ${normalizeText(node.title)} ${
    node.node_id
  }`.toLowerCase();

  if (node.question_type === "date") {
    if (hint.includes("start") || hint.includes("move in")) {
      return `When did you start living at ${label}?`;
    }
    if (
      hint.includes("end") ||
      hint.includes("stop") ||
      hint.includes("move out")
    ) {
      return `When did you stop living at ${label}?`;
    }
  }
  if (node.question_type === "boolean" && hint.includes("current")) {
    return `Is ${label} your current address?`;
  }
  return null;
}

/** The "is this your current ..." boolean sub-question of a group, if any. */
export function findGroupCurrentQuestion(
  group: OutgoingIntakeFormNode
): OutgoingIntakeFormNode | undefined {
  return groupChildQuestions(group).find(
    (q) =>
      q.question_type === "boolean" &&
      `${normalizeText(q.prompt)} ${q.node_id}`
        .toLowerCase()
        .includes("current")
  );
}

/** Whether an entry is flagged as the person's current/ongoing one. */
export function groupEntryIsCurrent(
  group: OutgoingIntakeFormNode,
  entryAnswers: Record<string, OutgoingIntakeAnswer>
): boolean {
  const currentQuestion = findGroupCurrentQuestion(group);
  return Boolean(
    currentQuestion && entryAnswers[currentQuestion.node_id]?.value === true
  );
}

/**
 * A timestamp used to order entries chronologically on the review screen.
 * Prefers the entry's start date; current/ongoing entries and entries without
 * a parseable date sort to the end.
 */
export function groupEntrySortKey(
  group: OutgoingIntakeFormNode,
  entryAnswers: Record<string, OutgoingIntakeAnswer>
): number {
  const startQuestion = groupChildQuestions(group).find(
    (q) => q.question_type === "date"
  );
  if (startQuestion) {
    const raw = entryAnswers[startQuestion.node_id]?.value;
    const parsed = raw ? Date.parse(String(raw)) : Number.NaN;
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * A concise one-line summary of an entry (e.g. "06/30/2026 - 07/20/2026")
 * shown on the review screen while an entry is collapsed. The current/ongoing
 * flag is surfaced separately as a badge. Falls back to the first meaningful
 * non-primary value when no dates are present.
 */
export function groupEntrySummary(
  group: OutgoingIntakeFormNode,
  entryAnswers: Record<string, OutgoingIntakeAnswer>
): string {
  const children = groupChildQuestions(group);
  const contextQuestion = children.find((q) => q.question_type === "address");
  const parts: string[] = [];

  const dateQuestions = children.filter((q) => q.question_type === "date");
  if (dateQuestions.length > 0) {
    const startValue = formatGroupAnswerValue(
      dateQuestions[0],
      entryAnswers[dateQuestions[0].node_id]
    );
    const endValue =
      dateQuestions.length > 1
        ? formatGroupAnswerValue(
            dateQuestions[1],
            entryAnswers[dateQuestions[1].node_id]
          )
        : "";
    if (startValue && endValue) parts.push(`${startValue} - ${endValue}`);
    else if (startValue) parts.push(`Since ${startValue}`);
    else if (endValue) parts.push(`Until ${endValue}`);
  }

  if (parts.length === 0) {
    for (const q of children) {
      if (q !== contextQuestion) {
        const value = formatGroupAnswerValue(q, entryAnswers[q.node_id]);
        if (value) {
          parts.push(value);
          break;
        }
      }
    }
  }

  return parts.join(" · ");
}

/**
 * Human-readable display value for a single answer, used on the review screen.
 * Returns an empty string when there's no meaningful value so empty rows can be
 * skipped. Mirrors the formatting used elsewhere for answer summaries.
 */
export function formatGroupAnswerValue(
  question: OutgoingIntakeFormNode,
  answer: OutgoingIntakeAnswer | null | undefined
): string {
  if (!answer) return "";
  const { value } = answer;
  const files = answer.files ?? [];

  const optionLabel = (id: unknown): string => {
    const raw = String(id ?? "");
    const opt = (question.options ?? []).find(
      (o) => String(o.option_id) === raw
    );
    return String(opt?.label ?? raw);
  };

  if (question.question_type === "file") {
    if (files.length === 0) return "";
    if (files.length === 1) return String(files[0]?.filename ?? "File");
    return `${files.length} files`;
  }
  if (question.question_type === "address") {
    const addr = asAddressValue(value);
    const parts = [
      normalizeText(addr.street),
      normalizeText(addr.city),
      normalizeText(addr.state),
      normalizeText(addr.zip_code),
      normalizeText(addr.country)
    ].filter(Boolean);
    return parts.join(", ");
  }
  if (question.question_type === "boolean") {
    if (typeof value !== "boolean") return "";
    return value ? "Yes" : "No";
  }
  if (question.question_type === "single_select") {
    return typeof value === "string" ? optionLabel(value) : "";
  }
  if (question.question_type === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    return arr.map(optionLabel).filter(Boolean).join(", ");
  }
  if (question.question_type === "date") {
    return formatDateOnly(value);
  }
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
}
